import React, { useEffect, useRef, useState } from 'react';
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDispatch } from 'react-redux';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../utils/loaderSlice';
import { NEW_MESSAGE_VIEW, NEW_MESSAGE_CREATE } from '../../../../constants/EmailServicesConst';
import TablePaginationActions from '../../../../shared/table-pagination/TablePaginationActions';

import SearchBar from '../../../../shared/search-bar/Search-Bar';
import useOutsideAlerter from '../../../../custom-hooks/useOutsideAlerter';
import { getSentEmails } from '../../../../utils/cmsUtils';
import { notificationManager } from '../../../../utils/NotificationUtils';
import './SentMessageList.scss';

const SENT_MESSAGE_FILTER = [
  {
    id: 'audience',
    title: 'Audience',
    field: 'audienceType',
    subMenu: [
      { id: 'Custom', name: 'Custom', selected: false },
      { id: 'Guest Segments', name: 'Guest Segments', selected: false },
    ],
  },
];

const getInitialFilterDataList = () =>
  SENT_MESSAGE_FILTER.map((f) => ({
    ...f,
    subMenu: f.subMenu.map((s) => ({ ...s, selected: false })),
  }));

const getAudienceLabel = (message) => {
  if (!message) return '—';
  const type = message?.audience?.type;
  if (type === 'GUEST_SEGMENTS') return 'Guest Segments';
  if (type === 'CUSTOM') return 'Custom';
  return message.recipientMode || 'Custom';
};

const formatSentTime = (sentAt) => {
  if (!sentAt) return '—';
  try {
    const date = new Date(sentAt * 1000);
    const now = new Date();
    const isToday =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();

    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).toLowerCase();

    if (isToday) return timeStr;

    const dateStr = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

    return `${dateStr}, ${timeStr}`;
  } catch {
    return '—';
  }
};

const SentMessageList = ({
  selectedHotel,
  onDrawerToggle,
  page,
  rowsPerPage,
  onsetPage,
  onsetRowsPerPage,
  refreshToggle,
}) => {
  const dispatch = useDispatch();
  const wrapperRef = useRef(null);

  const [sentMessages, setSentMessages] = useState([]);
  const [originalSentMessages, setOriginalSentMessages] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filterDataList, setFilterDataList] = useState(getInitialFilterDataList);
  const [visible, setVisible] = useState(false);
  const [popUp, setPopUp] = useState(false);
  const [selectedValue, setSelectedValue] = useState({ id: '', anchorEl: null, item: null });

  useOutsideAlerter(wrapperRef, visible, setPopUp);

  useEffect(() => {
    if (!selectedHotel) return;
    resetFilterDataList();
    setSearchText('');
    dispatch(stepUpLoaderCount());
    getSentEmails(selectedHotel)
      .then((res) => {
        const raw = res?.data?.listSentEmails || [];
        const withLabels = raw.map((m) => ({
          ...m,
          subjectBody: m.body,
          toEmails: Array.isArray(m.toEmails)
            ? m.toEmails.join(', ')
            : m.toEmails || (m.audience?.emails || []).join(', '),
          audienceLabel: getAudienceLabel(m),
          audienceType: getAudienceLabel(m),
        }));
        const sorted = [...withLabels].sort((a, b) => (b.sentAt || 0) - (a.sentAt || 0));
        setSentMessages(sorted);
        setOriginalSentMessages(sorted);
      })
      .catch(() => {
        notificationManager('danger', 'Failed to load sent messages.');
      })
      .finally(() => { dispatch(stepDownLoaderCount()); });
  }, [selectedHotel, refreshToggle]);

  const resetFilterDataList = () => {
    setFilterDataList(getInitialFilterDataList());
  };

  const handleSearchText = (text) => {
    setSearchText(text);
    const filtered = originalSentMessages.filter((row) =>
      (row.subject || '').toLowerCase().includes(text.toLowerCase()),
    );
    onsetPage(0);
    setSentMessages(filtered);
  };

  const filteredRecords = (_, field) => {
    setSearchText('');
    onsetPage(0);
    if (field === 'all') {
      setSentMessages(originalSentMessages);
      resetFilterDataList();
      return;
    }
    const activeFilters = {};
    filterDataList.forEach((filter) => {
      const selected = filter.subMenu.filter((s) => s.selected).map((s) => s.id);
      if (selected.length > 0) activeFilters[filter.field] = selected;
    });
    const filtered = originalSentMessages.filter((row) => {
      for (let [f, vals] of Object.entries(activeFilters)) {
        if (!vals.includes(row[f])) return false;
      }
      return true;
    });
    setSentMessages(filtered);
  };

  const audienceFilterCount =
    filterDataList.find((f) => f.field === 'audienceType')?.subMenu?.filter((s) => s.selected)?.length || 0;

  const handleChangePage = (_, newPage) => onsetPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    onsetRowsPerPage(parseInt(e.target.value, 10));
    onsetPage(0);
  };

  const openView = (item, e) => {
    e?.stopPropagation?.();
    onDrawerToggle(NEW_MESSAGE_VIEW, true, item);
    setSelectedValue({ id: '', anchorEl: null, item: null });
  };

  const handleCellMenuClick = (e, item) => {
    e.stopPropagation();
    setSelectedValue({ id: item.id, anchorEl: e.currentTarget, item });
  };

  const handleCloseMenu = () => setSelectedValue({ id: '', anchorEl: null, item: null });

  const paginatedRows =
    rowsPerPage > 0
      ? sentMessages.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : sentMessages;

  return (
    <div>
      <div className='mt-4 mb-4 sent-message-list-container'>
        <SearchBar
          type='CREATE'
          buttonName='New Message'
          setSearchText={handleSearchText}
          onOpenDrawer={(e) => {
            e.stopPropagation();
            onDrawerToggle(NEW_MESSAGE_CREATE, true, null);
          }}
          filterList={filterDataList}
          setFilterDataList={setFilterDataList}
          filteredRecoard={filteredRecords}
          displayAddOrEdit='show'
          visible={visible}
          setVisible={setVisible}
          popUp={popUp}
          setPopUp={setPopUp}
          placeHolder={sentMessages}
          setPlaceHolder={setSentMessages}
          globalPlaceHolder={originalSentMessages}
          dispatch={dispatch}
          displayText='sent messages'
          module='SENT_MESSAGES'
          searchText={searchText}
          setSearch={setSearchText}
          actionMenuItems={[]}
          handleAction={() => {}}
          mappingApi={() => {}}
          deviceFilterCount={0}
          statusFilterCount={0}
          audienceFilterCount={audienceFilterCount}
          setGlobalPlaceHolder={() => {}}
          setDrag={() => {}}
        />

        <div ref={wrapperRef} className='row mx-2 d-flex mt-4'>
          <TableContainer className='sent-message-table'>
            <Table className='list-table' aria-label='sent messages table'>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '45%' }}>Subject</TableCell>
                  <TableCell style={{ width: '25%' }} align='center'>
                    Audience
                  </TableCell>
                  <TableCell style={{ width: '20%' }} align='center'>
                    Sent Time
                  </TableCell>
                  <TableCell style={{ width: '10%' }} align='right' />
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedRows.map((item) => (
                  <TableRow
                    key={item.id}
                    className='border-white pointer'
                    onClick={(e) => openView(item, e)}
                  >
                    <TableCell style={{ width: '45%' }}>{item.subject}</TableCell>
                    <TableCell style={{ width: '25%' }} align='center'>
                      {item.audienceLabel}
                    </TableCell>
                    <TableCell style={{ width: '20%' }} align='center'>
                      {formatSentTime(item.sentAt)}
                    </TableCell>
                    <TableCell align='right' style={{ width: '10%' }}>
                      <IconButton
                        size='small'
                        onClick={(e) => openView(item, e)}
                      >
                        <VisibilityIcon style={{ height: '20px', width: '20px' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>

              <TableFooter>
                <TableRow className='paginator'>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 30, { label: 'All', value: -1 }]}
                    count={sentMessages.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: { 'aria-label': 'rows per page' },
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

export default SentMessageList;
